import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  FormHelperText
} from '@material-ui/core';
import { validatePhoneNumberAdvanced, getRemainingCooldown } from '../../utils/phoneValidation';
import { getAllCountries } from '../../utils/countryCodeUtils';

const PhoneInput = ({ onSendOTP, loading, error, cooldownUntil }) => {
  const [countryCode, setCountryCode] = useState('+1');
  const [selectedCountryCode, setSelectedCountryCode] = useState('US');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [validationError, setValidationError] = useState('');
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const [countryList, setCountryList] = useState([]);
  const [selectOpen, setSelectOpen] = useState(false);

  // Initialize country list
  useEffect(() => {
    const countries = getAllCountries();
    setCountryList(countries);
  }, []);

  // Handle cooldown timer
  useEffect(() => {
    if (cooldownUntil) {
      const remaining = getRemainingCooldown(cooldownUntil);
      setCooldownSeconds(remaining);

      if (remaining > 0) {
        const interval = setInterval(() => {
          const newRemaining = getRemainingCooldown(cooldownUntil);
          setCooldownSeconds(newRemaining);
          if (newRemaining <= 0) {
            clearInterval(interval);
          }
        }, 1000);

        return () => clearInterval(interval);
      }
    }
  }, [cooldownUntil]);

  // Compute display countries based on expand state
  const displayCountries = useMemo(() => {
    if (isExpanded) {
      return countryList; // Show all countries
    }

    // Show curated countries + "Other countries..." option
    const curated = countryList.filter(c => c.priority === 1);
    return [
      ...curated,
      {
        code: 'EXPAND',
        label: 'Other countries...',
        flag: '🌍',
        isExpander: true
      }
    ];
  }, [isExpanded, countryList]);

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Only numbers
    setPhoneNumber(value);
    setValidationError('');
  };

  const handleCountryChange = (e) => {
    const value = e.target.value;

    if (value === 'EXPAND') {
      setIsExpanded(true);
      setTimeout(() => setSelectOpen(true), 0);
      return;
    }

    const selectedCountry = countryList.find(c => c.countryCode === value);
    if (selectedCountry) {
      setCountryCode(selectedCountry.code);
      setSelectedCountryCode(selectedCountry.countryCode);
    }

    setValidationError('');
  };

  const handleSendOTP = () => {
    const fullPhoneNumber = `${countryCode}${phoneNumber}`;

    // Validate phone number
    if (!phoneNumber || phoneNumber.length === 0) {
      setValidationError('Please enter your phone number');
      return;
    }

    // Use enhanced validation with country context
    const validation = validatePhoneNumberAdvanced(fullPhoneNumber, selectedCountryCode);
    if (!validation.isValid) {
      setValidationError(validation.error);
      return;
    }

    // Check cooldown
    if (cooldownSeconds > 0) {
      setValidationError(`Please wait ${cooldownSeconds} seconds before requesting another OTP`);
      return;
    }

    setValidationError('');
    onSendOTP({ phoneNumber: fullPhoneNumber });
  };

  const isButtonDisabled = loading || cooldownSeconds > 0 || !phoneNumber;

  return (
    <Box className="phone-input-container">
      <Box style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
        <FormControl variant="outlined" style={{ minWidth: '140px' }}>
          <InputLabel>Country</InputLabel>
          <Select
            value={selectedCountryCode}
            open={selectOpen}
            onOpen={() => setSelectOpen(true)}
            onClose={() => setSelectOpen(false)}
            onChange={handleCountryChange}
            label="Country"
            MenuProps={{
              PaperProps: {
                style: { maxHeight: 400 }
              }
            }}
          >
            {displayCountries.map((country) => (
              <MenuItem
                key={country.countryCode || country.code}
                value={country.countryCode || 'EXPAND'}
                style={country.isExpander ? {
                  fontStyle: 'italic',
                  color: '#1976d2',
                  backgroundColor: '#f5f5f5'
                } : {}}
              >
                {country.flag} {country.code !== 'EXPAND' && country.code} {country.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          fullWidth
          variant="outlined"
          label="Phone Number"
          value={phoneNumber}
          onChange={handlePhoneChange}
          placeholder="1234567890"
          error={!!validationError || !!error}
          helperText={validationError || error}
          inputProps={{
            maxLength: 15,
            inputMode: 'numeric',
            pattern: '[0-9]*'
          }}
        />
      </Box>

      <Button
        fullWidth
        variant="contained"
        color="primary"
        onClick={handleSendOTP}
        disabled={isButtonDisabled}
        className="send-otp-btn"
      >
        {loading ? (
          <>
            Sending code <CircularProgress size={20} style={{ marginLeft: '10px', color: '#fff' }} />
          </>
        ) : cooldownSeconds > 0 ? (
          `Retry in ${cooldownSeconds}s`
        ) : (
          'Send code'
        )}
      </Button>

      <Box className="phone-input-help-text" style={{ marginTop: '15px', fontSize: '13px', color: '#666', textAlign: 'center' }}>
        You will receive a 6-digit code via SMS
      </Box>
    </Box>
  );
};

export default PhoneInput;
